// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fenêtres de structure : organisation, organe, fonction (catalogue codé),
// dispositif. Chaque création est vérifiée par le serveur (clé, double regard) ; ses refus sont affichés tels quels.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Building2, Layers, UserPlus, HandHeart, Power } from 'lucide-react';
import { Bouton } from '../../design/composants';
import {
  useDs, erreurDe, toArray, libelle, TYPES_ORGANISATION, STATUTS_JURIDIQUES, TYPES_ORGANES, TYPES_DISPOSITIF, CAPACITES, ETATS_ORGANISATION,
} from './api';
import { Fenetre, Champ, PiedFenetre, ErreurServeur, useLecture } from './commun';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** Soumission commune : erreur normalisée, bouton désactivé pendant l'envoi. */
function useEnvoi() {
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const envoyer = async (fn) => {
    setEnCours(true); setErreur(null);
    try { await fn(); } catch (e) { setErreur(erreurDe(e)); setEnCours(false); }
  };
  return { erreur, enCours, envoyer };
}

export function FenetreNouvelleOrganisation({ onFermer, onCree }) {
  const ds = useDs();
  const { erreur, enCours, envoyer } = useEnvoi();
  const [v, setV] = useState({ code: '', type: 'OEUVRES_SOCIALES', libelle: '', statut_juridique: 'A_QUALIFIER', mois_debut_exercice: 1 });
  const maj = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const pret = /^[A-Za-z][A-Za-z0-9]{1,7}$/.test(v.code.trim()) && v.libelle.trim().length >= 2;
  return (
    <Fenetre titre="Nouvelle organisation" Icone={Building2} onFermer={onFermer}>
      <ErreurServeur erreur={erreur} />
      <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
        L'organisation est créée en préparation. Un autre administrateur la met en service : ses organes sont validés avec elle.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 12 }}>
        <Champ libelle="Code" requis aide="2 à 8 lettres ou chiffres (ex. : OS).">
          <input className="rh-champ-saisie" value={v.code} maxLength={8} onChange={(e) => setV({ ...v, code: e.target.value.toUpperCase() })} />
        </Champ>
        <Champ libelle="Libellé" requis>
          <input className="rh-champ-saisie" value={v.libelle} maxLength={200} placeholder="Ex. : Œuvres sociales de la SOGAS" onChange={maj('libelle')} />
        </Champ>
      </div>
      <Champ libelle="Type" requis>
        <select className="rh-champ-saisie" value={v.type} onChange={maj('type')}>
          {Object.entries(TYPES_ORGANISATION).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
        </select>
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
        <Champ libelle="Statut juridique">
          <select className="rh-champ-saisie" value={v.statut_juridique} onChange={maj('statut_juridique')}>
            {Object.entries(STATUTS_JURIDIQUES).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Début de l'exercice">
          <select className="rh-champ-saisie" value={v.mois_debut_exercice} onChange={(e) => setV({ ...v, mois_debut_exercice: Number(e.target.value) })}>
            {MOIS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </Champ>
      </div>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" disabled={!pret || enCours}
          onClick={() => envoyer(async () => onCree(await ds.creerOrganisation({ ...v, code: v.code.trim(), libelle: v.libelle.trim() })))}>Créer l'organisation</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

const PASSAGES = { ACTIVE: ['EN_SOMMEIL', 'DISSOUTE'], EN_SOMMEIL: ['ACTIVE', 'DISSOUTE'] };
export const passagesOrganisation = (etat) => PASSAGES[etat] || [];

export function FenetreEtatOrganisation({ organisation, onFermer, onFait }) {
  const ds = useDs();
  const { erreur, enCours, envoyer } = useEnvoi();
  const choix = passagesOrganisation(organisation.etat);
  const [vers, setVers] = useState(choix[0] || '');
  const [motif, setMotif] = useState('');
  return (
    <Fenetre titre="Changer l'état de l'organisation" Icone={Power} onFermer={onFermer}>
      <ErreurServeur erreur={erreur} />
      <Champ libelle="Nouvel état" requis>
        <select className="rh-champ-saisie" value={vers} onChange={(e) => setVers(e.target.value)}>
          {choix.map((c) => <option key={c} value={c}>{libelle(ETATS_ORGANISATION, c)}</option>)}
        </select>
      </Champ>
      {vers === 'DISSOUTE' && <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--sg-danger)' }}>La dissolution est définitive : aucun acte ne sera plus possible dans cette organisation.</p>}
      <Champ libelle="Motif" requis aide="5 caractères au moins.">
        <textarea className="rh-champ-saisie" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante={vers === 'DISSOUTE' ? 'danger' : 'primaire'} disabled={!vers || motif.trim().length < 5 || enCours}
          onClick={() => envoyer(async () => onFait(await ds.changerEtatOrganisation(organisation.id, vers, motif.trim())))}>Confirmer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export function FenetreNouvelOrgane({ organisationId, onFermer, onCree }) {
  const ds = useDs();
  const { erreur, enCours, envoyer } = useEnvoi();
  const [type, setType] = useState('BUREAU');
  const [lib, setLib] = useState(TYPES_ORGANES.BUREAU);
  return (
    <Fenetre titre="Nouvel organe" Icone={Layers} onFermer={onFermer}>
      <ErreurServeur erreur={erreur} />
      <Champ libelle="Type d'organe" requis>
        <select className="rh-champ-saisie" value={type} onChange={(e) => { setType(e.target.value); setLib(TYPES_ORGANES[e.target.value]); }}>
          {Object.entries(TYPES_ORGANES).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
        </select>
      </Champ>
      <Champ libelle="Libellé" requis>
        <input className="rh-champ-saisie" value={lib} maxLength={120} onChange={(e) => setLib(e.target.value)} />
      </Champ>
      <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>Créé après la mise en service de l'organisation, l'organe doit être validé par un autre administrateur avant que ses mandats puissent être confirmés.</p>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" disabled={lib.trim().length < 2 || enCours}
          onClick={() => envoyer(async () => onCree(await ds.creerOrgane(organisationId, { type, libelle: lib.trim() })))}>Créer l'organe</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Ajout d'une fonction : seulement les fonctions du catalogue codé prévues pour ce type d'organe. */
export function FenetreNouvelleFonction({ organisation, organe, onFermer, onCree }) {
  const ds = useDs();
  const { erreur, enCours, envoyer } = useEnvoi();
  const catalogue = useLecture(() => ds.catalogueFonctions(organisation.type), [ds, organisation.type]);
  const existantes = new Set(toArray(organe.fonctions).map((f) => f.code));
  const possibles = toArray(catalogue.donnees).filter((f) => f.organe === organe.type && !existantes.has(f.code));
  const [code, setCode] = useState('');
  const choisi = possibles.find((f) => f.code === code) || null;
  return (
    <Fenetre titre={`Ajouter une fonction — ${organe.libelle}`} Icone={UserPlus} onFermer={onFermer}>
      <ErreurServeur erreur={erreur || catalogue.erreur} />
      {catalogue.donnees && !possibles.length && (
        <p style={{ margin: '0 0 12px', fontSize: 14 }}>Le catalogue ne prévoit aucune autre fonction pour un organe de ce type.</p>
      )}
      {possibles.length > 0 && (
        <Champ libelle="Fonction" requis aide="Le catalogue des fonctions et les droits qu'elles donnent sont fixés par le logiciel (modèle à valider par le bureau).">
          <select className="rh-champ-saisie" value={code} onChange={(e) => setCode(e.target.value)}>
            <option value="">Choisir une fonction</option>
            {possibles.map((f) => <option key={f.code} value={f.code}>{f.libelle}{f.unique_titulaire ? '' : ' (plusieurs membres)'}</option>)}
          </select>
        </Champ>
      )}
      {choisi && choisi.sensible && <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>Fonction sensible : elle donne accès à des informations confidentielles.</p>}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" disabled={!choisi || enCours}
          onClick={() => envoyer(async () => onCree(await ds.creerFonction(organe.id, { code })))}>Ajouter la fonction</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export function FenetreNouveauDispositif({ organisationId, onFermer, onCree }) {
  const ds = useDs();
  const { erreur, enCours, envoyer } = useEnvoi();
  const [v, setV] = useState({ code: '', type: 'PRET', libelle: '', texte_salarie: '', capacites: [] });
  const basculer = (c) => setV({ ...v, capacites: v.capacites.includes(c) ? v.capacites.filter((x) => x !== c) : [...v.capacites, c] });
  const pret = /^[A-Za-z][A-Za-z0-9_]{2,39}$/.test(v.code.trim()) && v.libelle.trim().length >= 2;
  return (
    <Fenetre titre="Nouveau dispositif" Icone={HandHeart} onFermer={onFermer} large>
      <ErreurServeur erreur={erreur} />
      <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
        Le dispositif est créé en brouillon. Son règlement (montants, plafonds, dates) se règle dans le Centre de configuration ; il est ensuite simulé, soumis, puis validé par une autre personne.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 12 }}>
        <Champ libelle="Code" requis aide="3 à 40 caractères : lettres, chiffres, « _ ».">
          <input className="rh-champ-saisie" value={v.code} maxLength={40} onChange={(e) => setV({ ...v, code: e.target.value.toUpperCase() })} />
        </Champ>
        <Champ libelle="Libellé" requis>
          <input className="rh-champ-saisie" value={v.libelle} maxLength={120} onChange={(e) => setV({ ...v, libelle: e.target.value })} />
        </Champ>
      </div>
      <Champ libelle="Type" requis>
        <select className="rh-champ-saisie" value={v.type} onChange={(e) => setV({ ...v, type: e.target.value })}>
          {Object.entries(TYPES_DISPOSITIF).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
        </select>
      </Champ>
      <Champ libelle="Texte présenté au salarié" aide="1 500 caractères au plus. Modifiable seulement en brouillon.">
        <textarea className="rh-champ-saisie" rows={3} maxLength={1500} value={v.texte_salarie} onChange={(e) => setV({ ...v, texte_salarie: e.target.value })} />
      </Champ>
      <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
        <legend style={{ fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 6 }}>Capacités</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
          {Object.entries(CAPACITES).map(([c, l]) => (
            <label key={c} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
              <input type="checkbox" checked={v.capacites.includes(c)} onChange={() => basculer(c)} />{l}
            </label>
          ))}
        </div>
      </fieldset>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" disabled={!pret || enCours}
          onClick={() => envoyer(async () => onCree(await ds.creerDispositif(organisationId, {
            code: v.code.trim(), type: v.type, libelle: v.libelle.trim(), capacites: v.capacites, ...(v.texte_salarie.trim() ? { texte_salarie: v.texte_salarie.trim() } : {}),
          })))}>Créer le brouillon</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}
